import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  Button,
  Skeleton,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Download,
  FolderZip,
  Print,
  TableRestaurant,
  Deck,
  MeetingRoom,
} from '@mui/icons-material';
import { collection, getDocs } from '../../services/firestoreCompat';
import { db } from '../../services/firebaseConfig';
import { QRCodeSVG } from 'qrcode.react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

interface TableDoc {
  id: string;
  number: number;
  area: string;
  status?: string;
}

const BASE_URL = '__SAHEEH_CUSTOMER_HOST_UNSET__/table';

// Shared
const INK = '#0a0a0a';
const CREAM = '#f4f4f0';
const MUTED = '#a1a1a1';

// Brand logo assets (served from web-admin/public/)
const SAHEEH_LOGO = '/saheeh_logo_w.svg';
const PIZZA_BRAND_LOGO = '/pizza_brand_logo.png';

// Cached image loader so we don't re-fetch the same logo for every card in a batch.
const imageCache: Record<string, HTMLImageElement> = {};
const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    if (imageCache[src] && imageCache[src].complete) return resolve(imageCache[src]);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { imageCache[src] = img; resolve(img); };
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });

type QrBrand = 'saheeh' | 'pizza_brand' | 'both';

interface BrandTheme {
  id: QrBrand;
  primary: string;        // accent (lime / amber)
  primaryDark: string;
  secondary?: string;     // used only by `both` — the "other" brand accent
  bgA: string;            // vertical gradient top
  bgB: string;            // vertical gradient mid
  bgC: string;            // vertical gradient bottom
  glowRgba: string;       // radial glow color
  glowSoft: string;
  wordmarkLeft: string;   // e.g. 'SUSHI'
  wordmarkRight: string;  // e.g. 'KI' (accent color)
  wordmarkFont: string;
  wordmarkItalic: boolean;
  tagline: string;
  emblemInitial: string;  // letter inside the QR center badge
  footerUrl: string;
  urlSuffix: string;      // appended to /table/N for deep-link
  fileLabel: string;      // for file names (Saheeh / PizzaPizzaBrand)
}

const THEMES: Record<Exclude<QrBrand, 'both'> | 'both', BrandTheme> = {
  saheeh: {
    id: 'saheeh',
    primary: '#00C896',
    primaryDark: '#6fb310',
    bgA: '#0d0f0a',
    bgB: '#0a0a0a',
    bgC: '#080a05',
    glowRgba: 'rgba(0, 200, 150,0.28)',
    glowSoft: 'rgba(0, 200, 150,0.22)',
    wordmarkLeft: 'SUSHI',
    wordmarkRight: 'KI',
    wordmarkFont: '"Inter Tight", "Inter", Arial, sans-serif',
    wordmarkItalic: false,
    tagline: 'S·U·S·H·I  &  B·O·W·L·S',
    emblemInitial: 'K',
    footerUrl: 'saheehpos.com  ·  Hürth',
    urlSuffix: '/menu/saheeh',
    fileLabel: 'Saheeh',
  },
  pizza_brand: {
    id: 'pizza_brand',
    primary: '#e8a94b',
    primaryDark: '#b07a20',
    bgA: '#0f0a06',
    bgB: '#0a0605',
    bgC: '#070403',
    glowRgba: 'rgba(232,169,75,0.30)',
    glowSoft: 'rgba(232,169,75,0.22)',
    wordmarkLeft: 'Pizza Don ',
    wordmarkRight: 'Vito',
    wordmarkFont: '"Playfair Display", "Times New Roman", serif',
    wordmarkItalic: true,
    tagline: 'P·I·Z·Z·A  ·  P·A·S·T·A  ·  I·N·S·A·L·A·T·A',
    emblemInitial: 'V',
    footerUrl: 'pizza_brand-huerth.de  ·  Hürth',
    urlSuffix: '/menu/pizza_brand',
    fileLabel: 'PizzaBrand',
  },
  both: {
    id: 'both',
    primary: '#00C896',
    primaryDark: '#6fb310',
    secondary: '#e8a94b',
    bgA: '#0c0d0a',
    bgB: '#0a0a0a',
    bgC: '#080706',
    glowRgba: 'rgba(0, 200, 150,0.26)',
    glowSoft: 'rgba(232,169,75,0.26)',
    wordmarkLeft: 'SUSHI',
    wordmarkRight: 'KI',
    wordmarkFont: '"Inter Tight", "Inter", Arial, sans-serif',
    wordmarkItalic: false,
    tagline: 'Two kitchens · One table',
    emblemInitial: '★',
    footerUrl: 'saheehpos.com  ·  pizza_brand-huerth.de  ·  Hürth',
    urlSuffix: '',
    fileLabel: 'Saheeh-PizzaBrand',
  },
};

/** Helper — rounded rect path */
const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

/** Renders a single premium QR card onto a canvas and returns a Blob */
const renderQrCardToCanvas = async (
  tableNumber: number,
  brand: QrBrand = 'saheeh',
): Promise<Blob> => {
  const theme = THEMES[brand];
  const W = 2000;
  const H = 2600;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No canvas context');

  // Preload logo image(s) before drawing the header
  const isDualBrand = theme.id === 'both';
  const [saheehLogo, pizza_brandLogo] = await Promise.all([
    (theme.id === 'saheeh' || isDualBrand) ? loadImage(SAHEEH_LOGO).catch(() => null) : Promise.resolve(null),
    (theme.id === 'pizza_brand' || isDualBrand) ? loadImage(PIZZA_BRAND_LOGO).catch(() => null) : Promise.resolve(null),
  ]);

  return new Promise((resolve, reject) => {

    // Parse rgba string to pull base color for creating full → transparent gradients
    const makeTransparent = (rgba: string): string =>
      rgba.replace(/rgba?\(([^)]+)\)/, (_m, inner) => {
        const parts = inner.split(',').map((s: string) => s.trim());
        return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, 0)`;
      });

    // ─── Ink card base ────────────────────────────
    ctx.fillStyle = INK;
    ctx.fillRect(0, 0, W, H);

    const vGrad = ctx.createLinearGradient(0, 0, 0, H);
    vGrad.addColorStop(0, theme.bgA);
    vGrad.addColorStop(0.5, theme.bgB);
    vGrad.addColorStop(1, theme.bgC);
    ctx.fillStyle = vGrad;
    ctx.fillRect(0, 0, W, H);

    // Top-left radial glow
    const glowA = ctx.createRadialGradient(260, 260, 0, 260, 260, 900);
    glowA.addColorStop(0, theme.glowRgba);
    glowA.addColorStop(1, makeTransparent(theme.glowRgba));
    ctx.fillStyle = glowA;
    ctx.fillRect(0, 0, W, H);

    // Bottom-right radial glow
    const glowB = ctx.createRadialGradient(W - 240, H - 240, 0, W - 240, H - 240, 900);
    glowB.addColorStop(0, theme.glowSoft);
    glowB.addColorStop(1, makeTransparent(theme.glowSoft));
    ctx.fillStyle = glowB;
    ctx.fillRect(0, 0, W, H);

    // Semi-transparent version of primary for brackets
    const hexToRgba = (hex: string, a: number) => {
      const h = hex.replace('#', '');
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r},${g},${b},${a})`;
    };

    // Corner brackets — in dual mode TL/BR use primary, TR/BL use secondary
    const bracketLen = 120;
    const bracketPad = 80;
    const bracketW = 6;
    const isDual = theme.id === 'both' && !!theme.secondary;
    const bracketC1 = hexToRgba(theme.primary, 0.6);
    const bracketC2 = hexToRgba(isDual ? theme.secondary! : theme.primary, 0.6);
    ctx.lineWidth = bracketW;
    ctx.lineCap = 'square';
    // Top-left
    ctx.strokeStyle = bracketC1;
    ctx.beginPath();
    ctx.moveTo(bracketPad, bracketPad + bracketLen);
    ctx.lineTo(bracketPad, bracketPad);
    ctx.lineTo(bracketPad + bracketLen, bracketPad);
    ctx.stroke();
    // Top-right
    ctx.strokeStyle = bracketC2;
    ctx.beginPath();
    ctx.moveTo(W - bracketPad - bracketLen, bracketPad);
    ctx.lineTo(W - bracketPad, bracketPad);
    ctx.lineTo(W - bracketPad, bracketPad + bracketLen);
    ctx.stroke();
    // Bottom-left
    ctx.strokeStyle = bracketC2;
    ctx.beginPath();
    ctx.moveTo(bracketPad, H - bracketPad - bracketLen);
    ctx.lineTo(bracketPad, H - bracketPad);
    ctx.lineTo(bracketPad + bracketLen, H - bracketPad);
    ctx.stroke();
    // Bottom-right
    ctx.strokeStyle = bracketC1;
    ctx.beginPath();
    ctx.moveTo(W - bracketPad - bracketLen, H - bracketPad);
    ctx.lineTo(W - bracketPad, H - bracketPad);
    ctx.lineTo(W - bracketPad, H - bracketPad - bracketLen);
    ctx.stroke();

    // ─── Brand logo(s) ────────────────
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    // Draw an image at a target HEIGHT, preserving aspect ratio, horizontally centered.
    const drawLogoCentered = (img: HTMLImageElement, centerY: number, targetH: number) => {
      const ratio = img.naturalWidth / img.naturalHeight;
      const h = targetH;
      const w = h * ratio;
      ctx.drawImage(img, (W - w) / 2, centerY - h / 2, w, h);
    };

    if (isDual) {
      // Side-by-side layout: Pizza Pizza Brand (left) | divider | Saheeh (right)
      const centerY = 370;
      const logoH = 240;
      const gap = 70;
      const pizzaRatio = pizza_brandLogo ? pizza_brandLogo.naturalWidth / pizza_brandLogo.naturalHeight : 1;
      const saheehRatio = saheehLogo ? saheehLogo.naturalWidth / saheehLogo.naturalHeight : 1;
      const pizzaW = logoH * pizzaRatio;
      const saheehW = logoH * saheehRatio;
      const totalW = pizzaW + saheehW + gap;
      const startX = (W - totalW) / 2;

      // Pizza Pizza Brand on the left
      if (pizza_brandLogo) {
        ctx.drawImage(pizza_brandLogo, startX, centerY - logoH / 2, pizzaW, logoH);
      }
      // Vertical gradient divider between the two logos
      const divX = startX + pizzaW + gap / 2;
      const divGrad = ctx.createLinearGradient(divX, centerY - 90, divX, centerY + 90);
      divGrad.addColorStop(0, 'rgba(255,255,255,0)');
      divGrad.addColorStop(0.5, hexToRgba(theme.secondary!, 0.8));
      divGrad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.strokeStyle = divGrad;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(divX, centerY - 90);
      ctx.lineTo(divX, centerY + 90);
      ctx.stroke();

      // Saheeh on the right
      if (saheehLogo) {
        ctx.drawImage(saheehLogo, startX + pizzaW + gap, centerY - logoH / 2, saheehW, logoH);
      }

      // Unified tagline beneath both
      ctx.fillStyle = MUTED;
      ctx.font = '600 38px "Inter", Arial, sans-serif';
      ctx.fillText(theme.tagline, W / 2, centerY + logoH / 2 + 70);

      // Decorative gradient rule
      const ruleY = centerY + logoH / 2 + 110;
      const ruleGrad = ctx.createLinearGradient(W / 2 - 100, 0, W / 2 + 100, 0);
      ruleGrad.addColorStop(0, theme.primary);
      ruleGrad.addColorStop(1, theme.secondary!);
      ctx.strokeStyle = ruleGrad;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(W / 2 - 100, ruleY);
      ctx.lineTo(W / 2 + 100, ruleY);
      ctx.stroke();
    } else {
      // Single brand: logo centered, tagline + rule underneath
      const logoImg = theme.id === 'saheeh' ? saheehLogo : pizza_brandLogo;
      if (logoImg) drawLogoCentered(logoImg, 340, 300);

      ctx.fillStyle = MUTED;
      ctx.font = '500 40px "Inter", Arial, sans-serif';
      ctx.fillText(theme.tagline, W / 2, 560);

      ctx.strokeStyle = theme.primary;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(W / 2 - 80, 600);
      ctx.lineTo(W / 2 + 80, 600);
      ctx.stroke();
    }

    // ─── QR render ────────────────────────────
    const url = `${BASE_URL}/${tableNumber}${theme.urlSuffix}`;
    const qrSize = 1200;
    const framePad = 70;
    const frameSize = qrSize + framePad * 2;
    const frameX = (W - frameSize) / 2;
    const frameY = isDual ? 720 : 680;

    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    document.body.appendChild(tempDiv);

    const ReactDOM = require('react-dom');
    const qrElement = React.createElement(QRCodeSVG, {
      value: url,
      size: qrSize,
      level: 'H',
      bgColor: '#ffffff',
      fgColor: INK,
    });
    ReactDOM.render(qrElement, tempDiv, () => {
      const svgEl = tempDiv.querySelector('svg');
      if (!svgEl) {
        document.body.removeChild(tempDiv);
        return reject(new Error('SVG not found'));
      }

      const svgData = new XMLSerializer().serializeToString(svgEl);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const urlObj = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        // Outer glow halo under QR frame — dual mode uses two offset halos
        if (isDual) {
          ctx.save();
          ctx.shadowColor = hexToRgba(theme.primary, 0.5);
          ctx.shadowBlur = 110;
          ctx.shadowOffsetX = -20;
          ctx.shadowOffsetY = 20;
          ctx.fillStyle = '#ffffff';
          roundRect(ctx, frameX, frameY, frameSize, frameSize, 48);
          ctx.fill();
          ctx.restore();
          ctx.save();
          ctx.shadowColor = hexToRgba(theme.secondary!, 0.5);
          ctx.shadowBlur = 110;
          ctx.shadowOffsetX = 20;
          ctx.shadowOffsetY = 20;
          ctx.fillStyle = '#ffffff';
          roundRect(ctx, frameX, frameY, frameSize, frameSize, 48);
          ctx.fill();
          ctx.restore();
        } else {
          ctx.save();
          ctx.shadowColor = hexToRgba(theme.primary, 0.55);
          ctx.shadowBlur = 120;
          ctx.shadowOffsetY = 20;
          ctx.fillStyle = '#ffffff';
          roundRect(ctx, frameX, frameY, frameSize, frameSize, 48);
          ctx.fill();
          ctx.restore();
        }

        // Clip and draw QR inside the white frame
        ctx.save();
        roundRect(ctx, frameX + framePad - 6, frameY + framePad - 6, qrSize + 12, qrSize + 12, 20);
        ctx.clip();
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(frameX + framePad - 6, frameY + framePad - 6, qrSize + 12, qrSize + 12);
        ctx.drawImage(img, frameX + framePad, frameY + framePad, qrSize, qrSize);
        ctx.restore();

        // ─── Below QR: Table number + bilingual subtitle ──────
        const belowY = frameY + frameSize + 150;

        ctx.fillStyle = theme.primary;
        ctx.font = '700 42px "Inter", Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('T A B L E   N U M B E R', W / 2, belowY);

        // Big table number
        ctx.fillStyle = CREAM;
        ctx.font = 'bold 220px "Inter Tight", "Inter", Arial, sans-serif';
        ctx.fillText(`Tisch ${tableNumber}`, W / 2, belowY + 200);

        // Bilingual subtitle
        ctx.fillStyle = MUTED;
        ctx.font = '500 54px "Inter", Arial, sans-serif';
        ctx.fillText('Scannen & bestellen  ·  Scan & order', W / 2, belowY + 290);

        // Bottom accent bar — gradient lime→amber in dual mode, solid in single
        if (isDual) {
          const barGrad = ctx.createLinearGradient(0, 0, W, 0);
          barGrad.addColorStop(0, theme.primary);
          barGrad.addColorStop(1, theme.secondary!);
          ctx.fillStyle = barGrad;
        } else {
          ctx.fillStyle = theme.primary;
        }
        ctx.fillRect(0, H - 14, W, 14);

        // Bottom URL micro-text
        ctx.fillStyle = '#6a6a66';
        ctx.font = '400 34px "Inter", Arial, sans-serif';
        ctx.fillText(theme.footerUrl, W / 2, H - 60);

        URL.revokeObjectURL(urlObj);
        document.body.removeChild(tempDiv);

        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas toBlob failed'));
        }, 'image/png');
      };
      img.onerror = () => {
        URL.revokeObjectURL(urlObj);
        document.body.removeChild(tempDiv);
        reject(new Error('Image load failed'));
      };
      img.src = urlObj;
    });
  });
};

const QrCodesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [tables, setTables] = useState<TableDoc[]>([]);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [brand, setBrand] = useState<QrBrand>('saheeh');
  const printRef = useRef<HTMLDivElement>(null);
  const activeTheme = THEMES[brand];

  const loadTables = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'restaurant_tables'));
      const loaded: TableDoc[] = [];
      snap.forEach((d) => {
        const data = d.data();
        loaded.push({
          id: d.id,
          number: data.number || data.tableNumber || 0,
          area: data.area || 'indoor',
          status: data.status,
        });
      });
      loaded.sort((a, b) => a.number - b.number);
      setTables(loaded);
    } catch (err) {
      console.error('[QR CODES] Failed to load tables:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  const handleDownloadSingle = async (tableNumber: number) => {
    setDownloading(`table-${tableNumber}`);
    try {
      const blob = await renderQrCardToCanvas(tableNumber, brand);
      saveAs(blob, `${activeTheme.fileLabel}-Tisch-${tableNumber}-QR.png`);
    } catch (err) {
      console.error('[QR CODES] Download failed:', err);
    }
    setDownloading(null);
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      const zip = new JSZip();
      for (const table of tables) {
        const blob = await renderQrCardToCanvas(table.number, brand);
        zip.file(`${activeTheme.fileLabel}-Tisch-${table.number}-QR.png`, blob);
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${activeTheme.fileLabel}-QR-Codes.zip`);
    } catch (err) {
      console.error('[QR CODES] ZIP download failed:', err);
    }
    setDownloadingAll(false);
  };

  const handlePrintAll = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const t = activeTheme;
    const isDual = t.id === 'both' && !!t.secondary;
    const secondary = t.secondary || t.primary;

    // Absolute URLs so the logos load inside the opened about:blank window
    const origin = window.location.origin;
    const saheehLogoUrl = `${origin}${SAHEEH_LOGO}`;
    const pizza_brandLogoUrl = `${origin}${PIZZA_BRAND_LOGO}`;

    const brandHtml = isDual
      ? `<div class="brand-row-dual">
           <img class="brand-logo brand-logo-pizza_brand" src="${pizza_brandLogoUrl}" alt="Pizza Pizza Brand" />
           <div class="brand-divider"></div>
           <img class="brand-logo brand-logo-saheeh" src="${saheehLogoUrl}" alt="Saheeh" />
         </div>`
      : t.id === 'saheeh'
        ? `<img class="brand-logo brand-logo-single" src="${saheehLogoUrl}" alt="Saheeh" />`
        : `<img class="brand-logo brand-logo-single" src="${pizza_brandLogoUrl}" alt="Pizza Pizza Brand" />`;

    const cards = tables
      .map(
        (table) => `
      <div class="qr-card">
        <div class="corner tl"></div>
        <div class="corner tr"></div>
        <div class="corner bl"></div>
        <div class="corner br"></div>
        <div class="glow glow-a"></div>
        <div class="glow glow-b"></div>

        <div class="header">
          <div class="brand">${brandHtml}</div>
          <div class="tagline">${t.tagline.replace(/·/g, '&middot;').replace(/&/g, '&amp;')}</div>
          <div class="rule"></div>
        </div>

        <div class="qr-wrap">
          <div class="qr-frame">
            <div class="qr-inner" id="qr-${table.number}"></div>
          </div>
        </div>

        <div class="footer">
          <div class="eyebrow">T A B L E &nbsp; N U M B E R</div>
          <div class="table-number">Tisch ${table.number}</div>
          <div class="subtitle">Scannen &amp; bestellen &nbsp;·&nbsp; Scan &amp; order</div>
          <div class="url">${t.footerUrl.replace(/·/g, '&middot;')}</div>
        </div>

        <div class="bottom-bar"></div>
      </div>
    `
      )
      .join('');

    // Per-brand helpers
    const primary = t.primary;
    const rgba = (hex: string, a: number) => {
      const h = hex.replace('#', '');
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r},${g},${b},${a})`;
    };
    const wordmarkFontImport =
      'https://fonts.googleapis.com/css2?family=Inter+Tight:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t.fileLabel} QR Codes</title>
        <link rel="stylesheet" href="${wordmarkFontImport}" />
        <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"><\/script>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          html, body { background: #0a0a0a; }
          body { font-family: 'Inter', -apple-system, Arial, sans-serif; }
          .qr-card {
            position: relative;
            page-break-after: always;
            width: 100%;
            height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 60px 40px;
            background:
              radial-gradient(circle at 15% 15%, ${rgba(primary, 0.28)}, transparent 45%),
              radial-gradient(circle at 85% 85%, ${rgba(secondary, 0.24)}, transparent 50%),
              linear-gradient(180deg, ${t.bgA} 0%, ${t.bgB} 50%, ${t.bgC} 100%);
            color: #f4f4f0;
            overflow: hidden;
          }
          .qr-card:last-child { page-break-after: auto; }

          /* Corner brackets — dual mode alternates colors */
          .corner {
            position: absolute; width: 90px; height: 90px;
            border: 5px solid ${rgba(primary, 0.55)};
          }
          .tl { top: 50px; left: 50px; border-right: 0; border-bottom: 0; border-color: ${rgba(primary, 0.55)}; }
          .tr { top: 50px; right: 50px; border-left: 0; border-bottom: 0; border-color: ${rgba(secondary, 0.55)}; }
          .bl { bottom: 50px; left: 50px; border-right: 0; border-top: 0; border-color: ${rgba(secondary, 0.55)}; }
          .br { bottom: 50px; right: 50px; border-left: 0; border-top: 0; border-color: ${rgba(primary, 0.55)}; }

          /* Soft glows */
          .glow { position: absolute; border-radius: 50%; filter: blur(70px); pointer-events: none; }
          .glow-a { width: 600px; height: 600px; top: -150px; left: -150px; background: ${rgba(primary, 0.25)}; }
          .glow-b { width: 700px; height: 700px; bottom: -200px; right: -180px; background: ${rgba(secondary, 0.22)}; }

          .header { text-align: center; position: relative; z-index: 2; }
          .brand { display: flex; flex-direction: column; align-items: center; gap: 12px; }
          .brand-logo { display: block; object-fit: contain; filter: drop-shadow(0 4px 12px rgba(0,0,0,0.4)); }
          .brand-logo-single { height: ${t.id === 'pizza_brand' ? '150px' : '120px'}; max-width: 520px; }
          .brand-row-dual {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            gap: 28px;
          }
          .brand-logo-pizza_brand { height: 130px; max-width: 200px; }
          .brand-logo-saheeh { height: 120px; max-width: 220px; }
          .brand-divider {
            width: 2px; height: 90px;
            background: linear-gradient(180deg, rgba(255,255,255,0) 0%, ${rgba(secondary, 0.8)} 50%, rgba(255,255,255,0) 100%);
          }
          .tagline {
            margin-top: 18px;
            color: #a1a1a1;
            font-size: 18px;
            font-weight: 500;
            letter-spacing: 0.12em;
          }
          .rule {
            width: 90px; height: 3px;
            background: linear-gradient(90deg, ${primary} 0%, ${secondary} 100%);
            margin: 18px auto 0;
          }

          .qr-wrap {
            margin: 40px 0;
            position: relative; z-index: 2;
          }
          .qr-frame {
            position: relative;
            background: #ffffff;
            border-radius: 28px;
            padding: 32px;
            box-shadow:
              0 0 0 2px ${rgba(primary, 0.4)},
              -18px 22px 80px -20px ${rgba(primary, 0.55)},
              18px 22px 80px -20px ${rgba(secondary, 0.5)},
              0 40px 80px rgba(0,0,0,0.55);
          }
          .qr-inner { width: 420px; height: 420px; display: flex; align-items: center; justify-content: center; }
          .qr-inner canvas { display: block; }

          .footer { text-align: center; position: relative; z-index: 2; }
          .eyebrow {
            color: ${primary};
            font-size: 14px;
            font-weight: 700;
            letter-spacing: 0.28em;
            margin-bottom: 12px;
          }
          .table-number {
            font-family: 'Inter Tight', sans-serif;
            font-size: 76px;
            font-weight: 800;
            color: #f4f4f0;
            letter-spacing: -0.02em;
            line-height: 1;
          }
          .subtitle {
            margin-top: 16px;
            color: #a1a1a1;
            font-size: 20px;
            font-weight: 500;
          }
          .url {
            margin-top: 28px;
            color: #6a6a66;
            font-size: 13px;
            letter-spacing: 0.08em;
          }

          .bottom-bar {
            position: absolute;
            bottom: 0; left: 0; right: 0;
            height: 8px;
            background: linear-gradient(90deg, ${primary} 0%, ${secondary} 100%);
          }

          @media print {
            .qr-card { height: 100vh; page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        ${cards}
        <script>
          window.onload = function() {
            ${tables
              .map(
                (table) => `
              QRCode.toCanvas(document.createElement('canvas'), '${BASE_URL}/${table.number}${t.urlSuffix}', {
                width: 420,
                margin: 1,
                errorCorrectionLevel: 'H',
                color: { dark: '#0a0a0a', light: '#ffffff' }
              }, function(err, canvas) {
                if (!err) {
                  document.getElementById('qr-${table.number}').appendChild(canvas);
                }
              });
            `
              )
              .join('')}
            setTimeout(function() { window.print(); }, 700);
          };
        <\/script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const indoorCount = tables.filter((t) => t.area === 'indoor').length;
  const outdoorCount = tables.filter((t) => t.area === 'outdoor').length;

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Skeleton width={300} height={36} sx={{ mb: 3 }} />
          <Skeleton height={80} sx={{ borderRadius: 2.5, mb: 2 }} />
          <Skeleton height={400} sx={{ borderRadius: 2.5 }} />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: '#e0e0e0', letterSpacing: '-0.02em' }}
            >
              QR Codes
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5 }}>
              Generate and download QR codes for table ordering
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Brand selector */}
            <Box
              sx={{
                display: 'inline-flex',
                p: '3px',
                borderRadius: 999,
                border: '1px solid #3a3d52',
                bgcolor: 'rgba(10,10,10,0.4)',
              }}
            >
              {(['saheeh', 'pizza_brand', 'both'] as QrBrand[]).map((b) => {
                const isActive = brand === b;
                const label = b === 'saheeh' ? 'Saheeh' : b === 'pizza_brand' ? 'Pizza Pizza Brand' : 'Both';
                const btnColor = THEMES[b].primary;
                return (
                  <Button
                    key={b}
                    size="small"
                    onClick={() => setBrand(b)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      borderRadius: 999,
                      px: 1.8,
                      minWidth: 'auto',
                      color: isActive ? '#0a0a0a' : '#9ca3af',
                      bgcolor: isActive ? btnColor : 'transparent',
                      '&:hover': {
                        bgcolor: isActive ? btnColor : 'rgba(255,255,255,0.05)',
                        color: isActive ? '#0a0a0a' : '#e0e0e0',
                      },
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<Print sx={{ fontSize: 16 }} />}
              onClick={handlePrintAll}
              disabled={tables.length === 0}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                borderColor: '#ddd',
                color: '#9ca3af',
                '&:hover': { borderColor: activeTheme.primary, color: activeTheme.primary },
              }}
            >
              Print All
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={
                downloadingAll ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <FolderZip sx={{ fontSize: 16 }} />
                )
              }
              onClick={handleDownloadAll}
              disabled={tables.length === 0 || downloadingAll}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                bgcolor: activeTheme.primary,
                color: '#0a0a0a',
                '&:hover': { bgcolor: activeTheme.primaryDark },
                '&:disabled': { bgcolor: '#e0e0e0', color: '#6b7280' },
              }}
            >
              {downloadingAll ? 'Generating ZIP...' : 'Download All ZIP'}
            </Button>
          </Box>
        </Box>

        {/* Summary Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Paper
            sx={{
              px: { xs: 1.5, md: 3 },
              py: 2,
              borderRadius: 2.5,
              border: '1px solid #3a3d52',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flex: '1 1 160px',
            }}
          >
            <TableRestaurant sx={{ color: activeTheme.primary, fontSize: 28 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#e0e0e0' }}>
                {tables.length}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                Total Tables
              </Typography>
            </Box>
          </Paper>
          <Paper
            sx={{
              px: { xs: 1.5, md: 3 },
              py: 2,
              borderRadius: 2.5,
              border: '1px solid #3a3d52',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flex: '1 1 160px',
            }}
          >
            <MeetingRoom sx={{ color: '#1976d2', fontSize: 28 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#e0e0e0' }}>
                {indoorCount}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                Indoor
              </Typography>
            </Box>
          </Paper>
          <Paper
            sx={{
              px: { xs: 1.5, md: 3 },
              py: 2,
              borderRadius: 2.5,
              border: '1px solid #3a3d52',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flex: '1 1 160px',
            }}
          >
            <Deck sx={{ color: '#ed6c02', fontSize: 28 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#e0e0e0' }}>
                {outdoorCount}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                Outdoor
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* QR Code Grid */}
        {tables.length === 0 ? (
          <Paper
            sx={{
              p: 6,
              borderRadius: 2.5,
              border: '1px solid #3a3d52',
              boxShadow: 'none',
              textAlign: 'center',
            }}
          >
            <TableRestaurant sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#6b7280', fontWeight: 600 }}>
              No tables found
            </Typography>
            <Typography variant="body2" sx={{ color: '#bbb', mt: 1 }}>
              Add tables in the Tables Management page first.
            </Typography>
          </Paper>
        ) : (
          <Box
            ref={printRef}
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 2.5,
            }}
          >
            {tables.map((table) => (
              <Paper
                key={table.id}
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid #3a3d52',
                  boxShadow: 'none',
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderColor: activeTheme.primary,
                  },
                }}
              >
                {/* Brand header */}
                <Box
                  sx={{
                    background:
                      brand === 'both' && activeTheme.secondary
                        ? `linear-gradient(90deg, ${activeTheme.primary} 0%, ${activeTheme.secondary} 100%)`
                        : activeTheme.primary,
                    py: 1,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 800,
                      fontSize: '1rem',
                      color: '#0a0a0a !important',
                      letterSpacing: 1,
                    }}
                  >
                    Tisch {table.number}
                  </Box>
                  <Chip
                    label={table.area}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(0,0,0,0.22)',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      height: 22,
                      textTransform: 'capitalize',
                      '& .MuiChip-label': { color: '#0a0a0a !important' },
                    }}
                  />
                </Box>

                {/* QR Code */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 3,
                    px: 2,
                  }}
                >
                  <QRCodeSVG
                    value={`${BASE_URL}/${table.number}${activeTheme.urlSuffix}`}
                    size={180}
                    level="H"
                    bgColor="#ffffff"
                    fgColor={INK}
                  />
                </Box>

                {/* URL preview */}
                <Box sx={{ px: 2, pb: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#aaa',
                      display: 'block',
                      textAlign: 'center',
                      fontSize: '0.65rem',
                      wordBreak: 'break-all',
                    }}
                  >
                    {BASE_URL}/{table.number}{activeTheme.urlSuffix}
                  </Typography>
                </Box>

                {/* Download button */}
                <Box sx={{ px: 2, pb: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    startIcon={
                      downloading === `table-${table.number}` ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <Download sx={{ fontSize: 16 }} />
                      )
                    }
                    onClick={() => handleDownloadSingle(table.number)}
                    disabled={downloading === `table-${table.number}`}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      borderColor: '#ddd',
                      color: '#9ca3af',
                      '&:hover': {
                        borderColor: activeTheme.primary,
                        color: activeTheme.primary,
                        bgcolor: `${activeTheme.primary}14`,
                      },
                    }}
                  >
                    {downloading === `table-${table.number}`
                      ? 'Generating...'
                      : 'Download PNG'}
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </AdminLayout>
  );
};

export default QrCodesPage;
